import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom"; // Import Link
import { signOut } from "firebase/auth";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
// Import Components
import StatCard from "../../components/dashboard/StatCard";
import Notification from "../../components/common/Notification";
import GuestDetailModal from "../../components/dashboard/GuestDetailModal";
import SortableHeaderCell from "../../components/dashboard/SortableHeaderCell";
import SortableTableHead from "../../components/dashboard/SortableTableHead";
import CustomToolTip from "../../components/dashboard/CustomToolTip";
import LoadingModal from "../../components/loading/LoadingModal";
// Utils
import {
  formatToTwoDigits,
  getStringFromBooleanWithDefault,
  getStringWithDefault,
} from "../../utils/string_util";
import * as XLSX from "sheetjs-style";
import { fetchRSVPList, updateRSVP } from "../../utils/firebase_util";
import { auth } from "../../utils/firebase/firebase";
import { useNotification } from "../../utils/notification_context";
import { useAuth } from "../../utils/firebase/AuthContext";

export default function Dashboard2() {
  const { userRole } = useAuth();
  const { showNotification, showErrorNotification } = useNotification();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [allRsvps, setAllRsvps] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({
    key: "guestName",
    direction: "ascending",
  });
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "success",
  });
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [xlsxScriptLoaded, setXlsxScriptLoaded] = useState(false);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://cdn.sheetjs.com/xlsx-latest/package/xlsx.mjs";
    script.type = "module";
    script.onload = () => {
      console.log("XLSX script loaded.");
      setXlsxScriptLoaded(true);
    };
    script.onerror = () => {
      console.error("Failed to load the XLSX script.");
      showErrorNotification("Could not load export library.", "error");
    };
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const docs = await fetchRSVPList();
        const data = docs.map((doc) => ({ docId: doc.id, ...doc.data() }));
        setAllRsvps(data);
      } catch (error) {
        console.error("Error loading dashboard data:", error);
        showErrorNotification("Failed to load data.", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getItemTotalPax = useCallback((item) => {
    if (!item || item.rsvp !== true) return 0;
    let totalPax = 1; // The guest themselves
    if (item.partnerAttending && item.partnerName) totalPax += 1;
    if (item.childrenAttending && item.childrenCount)
      totalPax += Number(item.childrenCount);
    return totalPax;
  }, []);

  const summaryStats = useMemo(() => {
    const acceptedInvites = allRsvps.filter((item) => item.rsvp === true);
    const totalPax = acceptedInvites.reduce(
      (sum, item) => sum + getItemTotalPax(item),
      0
    );
    const vegetarianCount = acceptedInvites
      .filter((item) => item.isVegetarian === true)
      .reduce((sum, item) => sum + getItemTotalPax(item), 0);
    return {
      totalInvites: allRsvps.length,
      acceptedCount: acceptedInvites.length,
      totalPax,
      estimatedTables: Math.ceil(totalPax / 10),
      vegetarianCount,
    };
  }, [allRsvps, getItemTotalPax]);

  const chartData = useMemo(() => {
    const acceptedRsvps = allRsvps.filter((r) => r.rsvp === true);
    const groomGuests = acceptedRsvps
      .filter((r) => r.invitedBy === "groom")
      .reduce((sum, rsvp) => sum + getItemTotalPax(rsvp), 0);
    const brideGuests = acceptedRsvps
      .filter((r) => r.invitedBy === "bride")
      .reduce((sum, rsvp) => sum + getItemTotalPax(rsvp), 0);
    const invitedByData = [
      { name: "Groom", value: groomGuests },
      { name: "Bride", value: brideGuests },
    ];
    const rsvpStatusData = [
      {
        name: "Accepted",
        value: allRsvps.filter((r) => r.rsvp === true).length,
      },
      {
        name: "Declined",
        value: allRsvps.filter((r) => r.rsvp === false).length,
      },
    ];
    return { invitedByData, rsvpStatusData };
  }, [allRsvps]);

  const sortedAndFilteredRsvps = useMemo(() => {
    let filteredData = [...allRsvps];
    if (statusFilter !== "all") {
      filteredData = filteredData.filter((item) =>
        statusFilter === "accepted" ? item.rsvp : !item.rsvp
      );
    }
    if (searchTerm) {
      filteredData = filteredData.filter((item) =>
        item.guestName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    filteredData.sort((a, b) => {
      const getSortableValue = (item, key) => {
        switch (key) {
          case "totalPax":
            return getItemTotalPax(item);
          case "createdAt":
            return item.createdAt.toDate();
          default:
            return item[key];
        }
      };

      const valA = getSortableValue(a, sortConfig.key);
      const valB = getSortableValue(b, sortConfig.key);

      let comparison = 0;
      if (valA > valB) {
        comparison = 1;
      } else if (valA < valB) {
        comparison = -1;
      }

      return sortConfig.direction === "ascending" ? comparison : -comparison;
    });

    return filteredData;
  }, [allRsvps, searchTerm, statusFilter, sortConfig, getItemTotalPax]);

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "ascending"
          ? "descending"
          : "ascending",
    }));
  };

  const handleExportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const wsData = allRsvps.map((item) => ({
      "Guest Name": getStringWithDefault(item.guestName),
      "Guest Contact": getStringWithDefault(item.guestContact),
      "Invited By": getStringWithDefault(item.invitedBy),
      "Partner Attending?": getStringFromBooleanWithDefault(
        item.partnerAttending
      ),
      "Partner Name": getStringWithDefault(item.partnerName),
      "Children Attending": getStringFromBooleanWithDefault(
        item.childrenAttending
      ),
      "Children Count": getStringWithDefault(item.childrenCount),
      "Baby Chair Count": getStringWithDefault(item.babyChairCount),
      "Is Vegetarian": getStringFromBooleanWithDefault(item.isVegetarian),
      "Dietary Restrictions": getStringWithDefault(item.dietaryRestrictions),
      "Total Pax": getItemTotalPax(item),
      RSVP: item.rsvp === true ? "Accepted" : "Declined",
      Date: formatDate(item.createdAt.toDate()),
    }));

    const ws = XLSX.utils.json_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "RSVP Data");

    XLSX.writeFile(wb, "RSVP_Data.xlsx");
    showNotification("Exporting to Excel...");
  };

  const formatDate = (date) => {
    return `${date.getFullYear()}-${formatToTwoDigits(
      date.getMonth() + 1
    )}-${formatToTwoDigits(date.getDate())} ${formatToTwoDigits(
      date.getHours()
    )}:${formatToTwoDigits(date.getMinutes())}`;
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      showNotification("You have been signed out!");
      setTimeout(() => {
        navigate("/login");
      }, 1500);
    } catch (err) {
      console.error("Logout error:", err);
      showErrorNotification("Failed to sign out.", "error");
    }
  };

  const renderCharts = () => {
    const COLORS_GENDER = ["#8884d8", "#82ca9d"];
    const COLORS_STATUS = ["#00C49F", "#FF8042", "#FFBB28"];

    const RADIAN = Math.PI / 180;
    const renderCustomizedLabel = ({
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      percent,
      value,
      name,
    }) => {
      const radiusInside = innerRadius + (outerRadius - innerRadius) * 0.5;
      const xInside = cx + radiusInside * Math.cos(-midAngle * RADIAN);
      const yInside = cy + radiusInside * Math.sin(-midAngle * RADIAN);

      const radiusOutside = outerRadius + 15;
      const xOutside = cx + radiusOutside * Math.cos(-midAngle * RADIAN);
      const yOutside = cy + radiusOutside * Math.sin(-midAngle * RADIAN);

      if (value === 0) return null;

      return (
        <>
          <text
            x={xInside}
            y={yInside}
            fill="white"
            textAnchor="middle"
            dominantBaseline="central"
            fontWeight="normal"
          >
            {value}
          </text>
          <text
            x={xOutside}
            y={yOutside}
            fill="#666"
            textAnchor={xOutside > cx ? "start" : "end"}
            dominantBaseline="central"
          >
            {`${name}: ${(percent * 100).toFixed(0)}%`}
          </text>
        </>
      );
    };

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white/70 p-4 rounded-xl shadow-sm backdrop-blur-sm">
          <h3 className="text-lg font-semibold text-center mb-4 text-[#931b1c]">
            Guest Breakdown (Attending)
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData.invitedByData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomizedLabel}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.invitedByData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS_GENDER[index % COLORS_GENDER.length]}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomToolTip />} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white/70 p-4 rounded-xl shadow-sm backdrop-blur-sm">
          <h3 className="text-lg font-semibold text-center mb-4 text-[#931b1c]">
            RSVP Status
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData.rsvpStatusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomizedLabel}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.rsvpStatusData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS_STATUS[index % COLORS_STATUS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const handleUpdateGuest = async (guestId, updatedData) => {
    try {
      setLoading(true);
      await updateRSVP(guestId, updatedData);

      setAllRsvps((prevRsvps) =>
        prevRsvps.map((rsvp) =>
          rsvp.docId === guestId ? { ...rsvp, ...updatedData } : rsvp
        )
      );

      setSelectedGuest((prevGuest) => ({ ...prevGuest, ...updatedData }));

      showNotification("Guest details updated successfully!");
    } catch (error) {
      console.error("Error updating guest:", error);
      showErrorNotification("Failed to update guest details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <GuestDetailModal
        guest={selectedGuest}
        onClose={() => setSelectedGuest(null)}
        getItemTotalPax={getItemTotalPax}
        onUpdate={handleUpdateGuest}
      />
      <div className="bg-[#f8ecdc] min-h-screen font-['Poppins'] text-gray-700">
        {notification.show && (
          <Notification
            message={notification.message}
            type={notification.type}
            onDismiss={() => setNotification({ ...notification, show: false })}
          />
        )}
        <div className="p-4 md:p-8 max-w-7xl mx-auto">
          <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-[#931b1c]">
                Guest Dashboard
              </h1>
              <p className="text-gray-500">RSVP Information Summary</p>
            </div>
            {/* --- NEW: Button group for navigation --- */}
            <div className="flex items-center gap-4 mt-4 md:mt-0">
              <Link
                to="/planner"
                className="px-4 py-2 bg-[#931b1c] text-white font-semibold rounded-lg hover:bg-opacity-90 flex items-center space-x-2 shadow-sm"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
                <span>Table Planner</span>
              </Link>
              <button
                onClick={handleLogout}
                className="mt-4 md:mt-0 px-4 py-2 bg-[#FDE8E4] border border-[#FFDDD7] rounded-lg text-sm font-semibold text-[#931b1c] hover:bg-[#FFDDD7] flex items-center space-x-2 shadow-sm"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
                <span>Logout</span>
              </button>
            </div>
            {/* --- END NEW --- */}
          </header>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              title="Accepted / Total RSVPs"
              value={`${summaryStats.acceptedCount} / ${summaryStats.totalInvites}`}
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-[#931b1c]"
                  fill="none"
                  viewBox="0 0 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
            />
            <StatCard
              title="Total Pax"
              value={summaryStats.totalPax}
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-[#931b1c]"
                  fill="none"
                  viewBox="0 0 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              }
            />
            <StatCard
              title="Vegetarian Guests (pax)"
              value={summaryStats.vegetarianCount}
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-[#931b1c] aspect-square"
                  fill="none"
                  viewBox="0 0 24"
                  stroke="currentColor"
                >
                  <g id="surface1">
                    <path d="M 1.417969 -0.0820312 C 2.347656 0.0351562 2.859375 0.535156 3.5625 1.125 C 3.789062 1.292969 3.789062 1.292969 4.015625 1.464844 C 8.398438 5.175781 10.679688 12.023438 11.8125 17.4375 C 11.855469 17.304688 11.894531 17.167969 11.9375 17.03125 C 12.128906 16.417969 12.324219 15.800781 12.515625 15.1875 C 12.613281 14.871094 12.613281 14.871094 12.714844 14.546875 C 13.257812 12.824219 13.832031 11.410156 15.035156 10.054688 C 15.125 9.953125 15.214844 9.847656 15.304688 9.742188 C 15.578125 9.429688 15.851562 9.121094 16.125 8.8125 C 16.210938 8.714844 16.296875 8.621094 16.386719 8.519531 C 16.855469 7.988281 17.332031 7.460938 17.8125 6.9375 C 16.167969 7.199219 14.28125 9.003906 13.3125 10.3125 C 13.1875 10.3125 13.066406 10.3125 12.9375 10.3125 C 12.398438 8.832031 12.253906 7.066406 12.921875 5.597656 C 13.90625 3.902344 15.332031 3.214844 17.0625 2.4375 C 17.480469 2.246094 17.902344 2.050781 18.320312 1.859375 C 18.589844 1.730469 18.863281 1.609375 19.132812 1.488281 C 19.792969 1.183594 20.382812 0.859375 20.96875 0.429688 C 21.5625 0 21.5625 0 22.089844 -0.0820312 C 22.5 0 22.5 0 22.875 0.375 C 23.300781 1.714844 23.3125 3.109375 23.308594 4.5 C 23.3125 4.691406 23.316406 4.882812 23.316406 5.078125 C 23.320312 7.34375 22.738281 8.894531 21.179688 10.523438 C 19.769531 11.875 18.527344 12.414062 16.570312 12.421875 C 16.171875 12.410156 15.773438 12.394531 15.375 12.375 C 15.339844 12.488281 15.308594 12.601562 15.273438 12.71875 C 15.222656 12.875 15.175781 13.035156 15.128906 13.195312 C 15.050781 13.453125 15.050781 13.453125 14.972656 13.71875 C 14.871094 14.050781 14.773438 14.382812 14.667969 14.714844 C 13.914062 17.183594 13.320312 19.632812 13.082031 22.207031 C 13.0625 22.433594 13.0625 22.433594 13.039062 22.660156 C 13.03125 22.796875 13.019531 22.933594 13.007812 23.070312 C 12.9375 23.4375 12.9375 23.4375 12.5625 24 C 11.976562 24.035156 11.976562 24.035156 11.4375 24 C 10.96875 22.734375 10.695312 21.472656 10.453125 20.144531 C 9.238281 13.839844 7.117188 6.605469 2.0625 2.25 C 1.816406 2.082031 1.566406 1.917969 1.3125 1.757812 C 0.75 1.3125 0.75 1.3125 0.65625 0.703125 C 0.777344 0.0273438 0.777344 0.0273438 1.417969 -0.0820312 Z M 1.417969 -0.0820312 " />
                  </g>
                </svg>
              }
            />
            <StatCard
              title="Estimated Tables"
              value={summaryStats.estimatedTables}
              icon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-[#931b1c]"
                  fill="none"
                  viewBox="0 0 24"
                  stroke="currentColor"
                >
                  <path d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              }
            />
          </div>
          {!loading && renderCharts()}
          <div className="bg-white/70 p-4 rounded-xl shadow-sm mb-6 flex flex-col md:flex-row items-center justify-between gap-4 md:space-y-0 backdrop-blur-sm">
            <div className="relative w-full md:w-auto">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-gray-400 absolute top-1/2 left-3 transform -translate-y-1/2"
                fill="none"
                viewBox="0 0 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by Guest Name"
                className="w-full md:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#931b1c]"
              />
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full sm:w-auto px-4 py-2 border border-[#FFDDD7] bg-[#FDE8E4] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#931b1c]"
              >
                <option value="all">All Statuses</option>{" "}
                <option value="accepted">Accepted</option>{" "}
                <option value="declined">Declined</option>
              </select>
              <button
                onClick={handleExportToExcel}
                disabled={!xlsxScriptLoaded}
                className="w-full sm:w-auto flex-shrink-0 px-4 py-2 bg-[#931b1c] text-white font-semibold rounded-lg hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#f8ecdc] focus:ring-[#931b1c] flex items-center justify-center space-x-2 disabled:bg-opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                <span>{xlsxScriptLoaded ? "Export" : "Loading..."}</span>
              </button>
            </div>
          </div>
          {loading ? (
            <LoadingModal isLoading={loading} />
          ) : (
            // <div className="flex items-center justify-center p-16">
            //   <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#931b1c]"></div>
            // </div>
            <div className="bg-white/70 rounded-xl shadow-sm overflow-x-auto backdrop-blur-sm">
              <table className="w-full text-sm">
                <SortableTableHead
                  sortConfig={sortConfig}
                  handleSort={handleSort}
                >
                  <SortableHeaderCell columnKey="guestName">
                    Guest Name
                  </SortableHeaderCell>
                  <SortableHeaderCell columnKey="rsvp">
                    RSVP Status
                  </SortableHeaderCell>
                  <SortableHeaderCell
                    columnKey="totalPax"
                    className="hidden sm:table-cell"
                  >
                    Total Pax
                  </SortableHeaderCell>
                  <th className="p-4 text-left whitespace-nowrap hidden md:table-cell">
                    Companion Details
                  </th>
                  <SortableHeaderCell
                    columnKey="invitedBy"
                    className="hidden lg:table-cell"
                  >
                    Invited By
                  </SortableHeaderCell>
                  <SortableHeaderCell
                    columnKey="createdAt"
                    className="hidden lg:table-cell"
                  >
                    Submitted At
                  </SortableHeaderCell>
                </SortableTableHead>
                <tbody>
                  {sortedAndFilteredRsvps.length > 0 ? (
                    sortedAndFilteredRsvps.map((item) => (
                      <tr
                        key={item.docId}
                        className="border-b border-[#FDE8E4] hover:bg-[#FDE8E4] cursor-pointer"
                        onClick={() => setSelectedGuest(item)}
                      >
                        <td className="p-4 font-medium text-[#931b1c]">
                          {item.guestName}
                        </td>
                        <td className="p-4">
                          <span
                            className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              item.rsvp
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {item.rsvp ? "Accepted" : "Declined"}
                          </span>
                        </td>
                        <td className="p-4 font-medium text-center text-gray-800 hidden sm:table-cell">
                          {getItemTotalPax(item)}
                        </td>
                        <td className="p-4 text-gray-600 hidden md:table-cell">
                          {item.partnerAttending && (
                            <div>Partner: {item.partnerName}</div>
                          )}
                          {item.childrenAttending && (
                            <div>Children: {item.childrenCount}</div>
                          )}
                        </td>
                        <td className="p-4 capitalize text-gray-600 hidden lg:table-cell">
                          {item.invitedBy}
                        </td>
                        <td className="p-4 text-gray-500 hidden lg:table-cell">
                          {item.createdAt.toDate().toLocaleDateString()}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center p-8 text-gray-500">
                        No matching records found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
